import {Injectable} from '@angular/core';
import {Properties} from 'posthog-js';
import {distinctUntilChanged, map} from 'rxjs';

import * as APIM from '@common/utils/dist/models/api-messages.js';
import * as PL2 from '@common/utils/dist/models/models-pl2.js';
import {KeyUtilsPL2 as KU} from '@common/utils/dist/utils/key-utils-pl2.js';
import {UtilsPL2 as U} from '@common/utils/dist/utils/utils-pl2.js';

import {PosthogWrapperService} from '@services/posthog-wrapper.service';
import {LabReportUtils as LRU} from '@utils/lab-report-utils';
import {RouteStore} from '@stores/route.store';
import {EntryStore} from '@stores/entry.store';

export enum PosthogEventType {
  ASSIGNMENT_CREATED = 'assignment_created',
  VERSION_CREATED = 'version_created',
  PRIMARY_VIEWED = 'primary_viewed',
  INVITE_ACCEPTED = 'invite_accepted',
  ASSIGNMENT_SENT = 'assignment_sent',
  SECTION_CREATED = 'section_created',
  MYLESSON_CREATED = 'mylesson_created',
  COLLECTION_CREATED = 'collection_created',
  CLASS_CREATED = 'class_created',
  LESSON_TRANSLATED = 'lesson_translated',
  READING_LEVEL_ADJUSTED = 'readng_level_adjusted',
  LESSON_PLAN_GENERATED = 'lesson_plan_generated',
  RUBRIC_GENERATED = 'rubric_generated',
  NGSS_ALIGNMENT_GENERATED = 'ngss_alignment_generated',
  TEKS_ALIGNMENT_GENERATED = 'teks_alignment_generated',
  EXIT_TICKET_GENERATED = 'exit_ticket_generated',
  QUIZ_GENERATED = 'quiz_generated',
  FAMILY_LETTER_GENERATED = 'family_letter_generated',
  OTHER_GENERATED = 'other_generated',
  AI_CHAT_OPENED = 'ai_chat_opened',
  AI_CHAT_MESSAGE_SENT = 'ai_chat_message_sent',
}

export interface PosthogEvent<T> {
  eventName: string;
  data: T;
}

export interface LessonPosthogEventData {
  mCats: string[];
  cat: string[];
  lessonName: string;
  lessonSrcId: string;
}

type EmptyPosthogEventData = null;

export interface SectionPosthogEventData {
  type: string;
}

export interface PosthogEventBuilder<I, D> {
  build(input: I): Array<PosthogEvent<D>>;
}

function buildLessonPosthogEvent(
  eventName: string,
  entry: PL2.LabReportEntry | PL2.AssignmentEntry,
  srcId: string,
): PosthogEvent<LessonPosthogEventData> {
  return {
    eventName: eventName,
    data: U.excludeEmptyProperties({
      mCats: entry.mCats,
      cat: entry.cat,
      lessonName: entry.n,
      lessonSrcId: srcId,
    }),
  };
}

export type CreatePosthogEvent =
  | LessonPosthogEventData
  | SectionPosthogEventData
  | EmptyPosthogEventData;

export class CreatePosthogEventBuilder implements PosthogEventBuilder<
  Array<PL2.AnyPartialEntryWithId>,
  CreatePosthogEvent
> {
  build(
    entries: Array<PL2.AnyPartialEntryWithId>,
  ): Array<PosthogEvent<CreatePosthogEvent>> {
    return (entries ?? [])
      .map((entry) => {
        if (!KU.isTyped(entry)) {
          return null;
        }

        if (LRU.isVersion(entry as any)) {
          return buildLessonPosthogEvent(
            PosthogEventType.VERSION_CREATED,
            entry as PL2.LabReportEntry,
            (entry as PL2.Referenceable).refId,
          );
        }

        const entryType = KU.type(entry);
        if (KU.isSectionType(entryType)) {
          return {
            eventName: PosthogEventType.SECTION_CREATED,
            data: {type: entryType},
          };
        }

        switch (entryType) {
          case PL2.EntryType.Class:
            return {eventName: PosthogEventType.CLASS_CREATED, data: null};
          case PL2.EntryType.LabReport:
            return {eventName: PosthogEventType.MYLESSON_CREATED, data: null};
          case PL2.EntryType.Course:
            return {eventName: PosthogEventType.COLLECTION_CREATED, data: null};
          case PL2.EntryType.Assignment:
            return buildLessonPosthogEvent(
              PosthogEventType.ASSIGNMENT_CREATED,
              entry as PL2.AssignmentEntry,
              (entry as PL2.Referenceable).refId,
            );
          default:
            return null;
        }
      })
      .filter((event) => !U.isEmpty(event));
  }
}

export type ReadPosthogEvent = LessonPosthogEventData;

@Injectable()
export class ReadPosthogEventBuilder implements PosthogEventBuilder<
  Array<APIM.IndexQuery>,
  ReadPosthogEvent
> {
  constructor(
    private routeStore: RouteStore,
    private entryStore: EntryStore,
  ) {
    this.routeStore.state$
      .pipe(
        map((state) => state?.containerId),
        distinctUntilChanged(),
      )
      .subscribe(() => (this.lastViewedPK = null));
  }

  /** Container pK of the last emitted primary_viewed; one event per container visit. */
  private lastViewedPK: string | null = null;

  build(
    queries: Array<APIM.IndexQuery>,
  ): Array<PosthogEvent<ReadPosthogEvent>> {
    return (queries ?? [])
      .map((query: any) => {
        if (U.isEmpty(query.mId) || U.isEmpty((query as PL2.EntryId).sK)) {
          return null;
        }

        const pK = KU.stringFromKey(query as PL2.EntryId);
        const entry = (this.entryStore.state()[pK]?.e ?? query) as PL2.AnyEntry;
        if (
          LRU.isVersionByAuthor(entry) &&
          this.routeStore.state()?.containerId === pK &&
          this.lastViewedPK !== pK
        ) {
          this.lastViewedPK = pK;
          return buildLessonPosthogEvent(
            PosthogEventType.PRIMARY_VIEWED,
            entry as PL2.LabReportEntry,
            (entry as PL2.Sourceable).srcId,
          );
        } else {
          return null;
        }
      })
      .filter((event) => !U.isEmpty(event));
  }
}

export type CopyPosthogEvent = EmptyPosthogEventData | LessonPosthogEventData;

@Injectable()
export class CopyPosthogEventBuilder implements PosthogEventBuilder<
  Array<APIM.EntryCopy>,
  CopyPosthogEvent
> {
  constructor(private entryStore: EntryStore) {}

  build(copies: Array<APIM.EntryCopy>): Array<PosthogEvent<CopyPosthogEvent>> {
    return (copies ?? [])
      .map((copy) => {
        switch (copy.copyType) {
          case APIM.CopyType.AcceptInvite:
            return {
              eventName: PosthogEventType.INVITE_ACCEPTED,
              data: null,
            };
          case APIM.CopyType.VToSCCopy:
            return {
              eventName: PosthogEventType.ASSIGNMENT_SENT,
              data: null,
            };
          case APIM.CopyType.SrcToVCopy:
            const sToV = copy as APIM.SrcToVersionCopy;
            const id = {mId: sToV.lMId, sK: sToV.lSK};
            const entry =
              this.entryStore.state()[KU.stringFromKey(id)]?.e ??
              (id as PL2.LabReportEntry);
            return buildLessonPosthogEvent(
              PosthogEventType.VERSION_CREATED,
              entry as PL2.AssignmentEntry,
              (entry as PL2.Sourceable).srcId,
            );
          default:
            return null;
        }
      })
      .filter((event) => !U.isEmpty(event));
  }
}

export type AiPosthogEvent = EmptyPosthogEventData;

export class AiPosthogEventBuilder implements PosthogEventBuilder<
  APIM.EventIntent,
  AiPosthogEvent
> {
  constructor() {}

  build(
    int: APIM.EventIntent,
    dt?: APIM.DocumentTypeOption,
  ): Array<PosthogEvent<AiPosthogEvent>> {
    switch (int) {
      case APIM.EventIntent.AITranslateLesson:
        return [
          {
            eventName: PosthogEventType.LESSON_TRANSLATED,
            data: null,
          },
        ];
      case APIM.EventIntent.AIAdjustReadingLevel:
        return [
          {
            eventName: PosthogEventType.READING_LEVEL_ADJUSTED,
            data: null,
          },
        ];
      case APIM.EventIntent.AIGenerateNewDocument:
        let eventName = '';
        switch (dt) {
          case APIM.DocumentTypeOption.ExitTicket:
            eventName = PosthogEventType.EXIT_TICKET_GENERATED;
            break;
          case APIM.DocumentTypeOption.GradingRubric:
            eventName = PosthogEventType.RUBRIC_GENERATED;
            break;
          case APIM.DocumentTypeOption.LessonPlan:
            eventName = PosthogEventType.LESSON_PLAN_GENERATED;
            break;
          case APIM.DocumentTypeOption.LessonSummaryForFamilies:
            eventName = PosthogEventType.FAMILY_LETTER_GENERATED;
            break;
          case APIM.DocumentTypeOption.NgssStandardsAlignment:
            eventName = PosthogEventType.NGSS_ALIGNMENT_GENERATED;
            break;
          case APIM.DocumentTypeOption.Quiz:
            eventName = PosthogEventType.QUIZ_GENERATED;
            break;
          case APIM.DocumentTypeOption.TeksStandardsAlignment:
            eventName = PosthogEventType.TEKS_ALIGNMENT_GENERATED;
            break;
          case APIM.DocumentTypeOption.Other:
            eventName = PosthogEventType.OTHER_GENERATED;
            break;
          case APIM.DocumentTypeOption.Unknown:
          default:
            return null;
        }
        return [
          {
            eventName: eventName,
            data: null,
          },
        ];
      default:
        return null;
    }
  }
}

@Injectable()
export class PosthogSpy {
  constructor(private posthogWrapperService: PosthogWrapperService) {}

  capture<T>(events: Array<PosthogEvent<T>>): void {
    if (U.isEmpty(events)) {
      return;
    }

    events.forEach((event) =>
      this.posthogWrapperService.capture(
        event.eventName,
        this._toRecord(event.data),
      ),
    );
  }

  private _toRecord<T>(data: T): Properties {
    const serialized: Record<string, any> = {};
    if (!U.isEmpty(data)) {
      for (const [key, value] of Object.entries(data)) {
        serialized[key] = value as any;
      }
    }
    return serialized;
  }
}
